<script setup lang="ts">
/**
 * Main view of the account side panel. Shows the partner profile
 * (avatar + ragione sociale + partita iva), the "Accesso" section
 * with the email-of-access action, and the "Pagamenti" section
 * with the IBAN action plus holder + bank rows. The danger button
 * triggers logout.
 *
 * Pure presentation. The shell (`AccountPanel.vue`) owns the
 * navigation state and re-emits view changes to the host page.
 *
 * Mirrors the design bundle's `AccountPanel.jsx::PanelMain`.
 */
import { useI18n } from 'vue-i18n'
import type { PortalSelfResponse } from '@/stores/portalSelf.store'

defineProps<{
  partner: PortalSelfResponse
}>()

const emit = defineEmits<{
  go: [view: 'iban' | 'email']
  logout: []
}>()

const { t } = useI18n({ useScope: 'global' })
</script>

<template>
  <div>
    <div class="pp-panel__profile">
      <div class="pp-panel__avatar">{{ partner.initials }}</div>
      <div>
        <div class="pp-panel__name">{{ partner.ragione_sociale }}</div>
        <div class="pp-panel__sub mono">{{ partner.partita_iva }}</div>
      </div>
    </div>

    <div class="pp-panel__sect">
      <div class="pp-panel__sect-h">{{ t('portal.account_panel.main.section_access') }}</div>
      <button type="button" class="pp-panel__action" @click="emit('go', 'email')">
        <div class="pp-panel__action-l">
          <div class="pp-panel__action-t">
            {{ t('portal.account_panel.main.access_email_action') }}
          </div>
          <div class="pp-panel__action-s mono">{{ partner.email }}</div>
        </div>
        <svg
          class="pp-panel__action-c"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
      <div class="pp-panel__hint">
        {{ t('portal.account_panel.main.access_email_hint') }}
      </div>
    </div>

    <div class="pp-panel__sect">
      <div class="pp-panel__sect-h">{{ t('portal.account_panel.main.section_payments') }}</div>
      <button type="button" class="pp-panel__action" @click="emit('go', 'iban')">
        <div class="pp-panel__action-l">
          <div class="pp-panel__action-t">
            {{ t('portal.account_panel.main.payments_iban_action') }}
          </div>
          <div class="pp-panel__action-s mono">{{ partner.iban }}</div>
        </div>
        <svg
          class="pp-panel__action-c"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
      <div class="pp-panel__row">
        <span class="pp-panel__k">{{ t('portal.account_panel.main.payments_holder') }}</span>
        <span class="pp-panel__v">{{ partner.account_holder }}</span>
      </div>
      <div class="pp-panel__row">
        <span class="pp-panel__k">{{ t('portal.account_panel.main.payments_bank') }}</span>
        <span class="pp-panel__v">{{ partner.bank }}</span>
      </div>
    </div>

    <div class="pp-panel__sect">
      <button type="button" class="pp-panel__danger" @click="emit('logout')">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        {{ t('portal.account_panel.main.logout_button') }}
      </button>
    </div>
  </div>
</template>
